import React from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { cn } from "../../../utils/cn.ts"
import { EntityIcon } from "../map/EntityIcon.tsx"
import { selectActiveLayers, toggleLayer } from "../redux/global-map.slice"
import type { EntityType } from "../redux/global-map.types"

// Order matches the reference mockup: Lead (pin, left) then Project (house,
// right) — not the "Projects & Leads" label's own word order.
const LAYER_TOGGLE_ORDER: EntityType[] = ["lead", "project"]

// A selected chip tints toward its OWN entity colour (Lead blue, Project
// green) rather than toward one shared accent, so the pair reads as the same
// semantic grammar the markers, the legend and the KPI tiles already use.
// Spelled out as whole class strings because Tailwind only sees literals.
const SELECTED_CHIP_CLASS: Record<EntityType, string> = {
  lead: "border-blue-300 bg-blue-50 text-blue-700",
  project: "border-green-300 bg-green-50 text-green-700"
}

/**
 * Replaces the "Projects & Leads" `FacetMultiSelectFilter` dropdown with two
 * independent toggle chips, per explicit product direction: a "Lead" chip and
 * a "Project" chip, each carrying the same `EntityIcon` (Leads/Projects KPI
 * icon+color pair) every other Global Map surface uses, each independently
 * on/off, both on by default. They share the filter row's own control
 * geometry (36px tall, 6px radius) and tint toward their own entity colour
 * when selected, rather than sitting on one shared backdrop. Dispatches
 * the same
 * `toggleLayer` action (and reads the same `activeLayers` state,
 * `selectActiveLayers`) the retired standalone `LayerSelector` pill pair and
 * the dropdown it replaced both used — only the visual presentation and the
 * dropped facet counts change, never the underlying 0/1/2-choice semantics
 * ("zero is a valid, allowed selection" — see `toggleLayer`'s own comment in
 * `global-map.slice.ts`).
 *
 * `aria-pressed` + explicit Enter/Space `onKeyDown` handling follows the
 * `LayerSelector` precedent this supersedes: a native `<button>` already
 * turns a focused Enter/Space into a click in a real browser, but jsdom does
 * not simulate that default action, so the handler drives the toggle
 * directly (with `preventDefault` so a real keypress cannot double-toggle).
 */
export function LayerToggleButtons() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const activeLayers = useAppSelector(selectActiveLayers)

  const toggle = (entityType: EntityType) => {
    dispatch(toggleLayer(entityType))
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    entityType: EntityType
  ) => {
    if (
      event.key === "Enter" ||
      event.key === " " ||
      event.key === "Spacebar"
    ) {
      event.preventDefault()
      toggle(entityType)
    }
  }

  return (
    <div
      role="group"
      aria-label={t("globalMap.filters.projectsAndLeads")}
      className="flex shrink-0 items-center gap-2"
      data-testid="global-map-layer-toggle-group"
    >
      {LAYER_TOGGLE_ORDER.map((entityType) => {
        const pressed = activeLayers.includes(entityType)
        const label = t(`globalMap.entityLabel.${entityType}`)
        return (
          <button
            key={entityType}
            type="button"
            aria-pressed={pressed}
            onClick={() => toggle(entityType)}
            onKeyDown={(event) => handleKeyDown(event, entityType)}
            className={cn(
              "flex h-10 cursor-pointer items-center gap-2 rounded-lg border px-3.5 text-[13px] font-medium shadow-[0_2px_6px_rgba(15,23,42,0.06)] transition-colors",
              pressed
                ? SELECTED_CHIP_CLASS[entityType]
                : // Unselected is a plain, fully legible white chip — never a
                  // dimmed one, which read as disabled rather than as "off".
                  "border-[#E2E8F0] bg-white text-slate-700 hover:border-slate-300 hover:text-slate-900"
            )}
          >
            {/* The same swatch the marker and the legend use, so all three
                read as one identity. */}
            <EntityIcon entityType={entityType} className="size-5" />
            {label}
          </button>
        )
      })}
    </div>
  )
}

export default LayerToggleButtons
